/**
 * The layered "bloom" (lane C0-VFX-PREP): the building blocks of docs/art/auras-ref (each aura = a ground ring, a
 * swirl round the body, a rising column, motes) and docs/art/skills-ref (crescents as tall as the figure, rings round
 * the caster, pillars on the big blows), redrawn in our own pixel language: hard cells, the element ramp's 5 tones,
 * a dark outline cell on the outer edge, dither fades. Refs are ideas only; no shape is traced from them.
 *
 * Used by draw.ts on landed blows (by weight: medium = ring + swirl, heavy = + column and a second ring), on spins
 * (a swirl round the caster), and on body auras (heals and buffs: ring + swirl + column + motes).
 * Every piece is a cached picture (one drawImage), so the bloom costs a handful of draws a frame.
 */
import { easeOut, hash2, span } from '../noise';
import { foeX, P, type InkLayer, type InkScene } from '../samples';
import { detailQuality, drawPic, gone, pic, ringPic, type Pic } from './draw';
import type { Ramp } from './ramps';
import { cellOf, dot } from './sprites';

/** Swirl phase steps (a full turn) and fade steps. */
const PHASES = 8;

/**
 * Two ribbons spiralling up round a vertical axis (radius `rx` art px, height `h`), `turns` turns over the height,
 * one half of the picture: the back half (`front` false, drawn under the figure) or the front (over it). Each ribbon is
 * three cells thick: the light head, the body, a dark outline cell on its lower edge. `fade` 0–3 dithers it away.
 */
function swirlPic(R: Ramp, rx: number, h: number, phase: number, front: boolean, fade: number, turns = 0.85): Pic {
  const c = cellOf('world');
  const rxb = Math.round(rx / 4) * 4;
  const hb = Math.round(h / 8) * 8;
  return pic(`swirl|${R[0]}|${rxb}|${hb}|${phase}|${front ? 1 : 0}|${fade}|${turns}|${c}`, -rxb - 4, -hb - rxb * 0.5 - 4, rxb + 4, rxb * 0.5 + 12, (set) => {
    const n = Math.round(hb * 6);
    for (let rib = 0; rib < 2; rib++)
      for (let i = 0; i < n; i++) {
        const u = i / n;
        const a = (phase / PHASES) * Math.PI * 2 + rib * Math.PI + u * turns * Math.PI * 2;
        const isFront = Math.sin(a) > 0;
        if (isFront !== front) continue;
        // Thinner at the ends, so a ribbon tapers in and out like the refs' wind and water bands.
        const body = Math.sin(Math.PI * u);
        if (body < 0.12) continue;
        if (fade > 0 && (i + rib * 3) % 4 < fade) continue;
        const r = rxb * (0.7 + 0.3 * body);
        const x = Math.round((Math.cos(a) * r) / c) * c;
        const y = Math.round((-u * hb + Math.sin(a) * r * 0.42) / c) * c;
        // A broad band (the refs' wind and water ribbons, not a wire): up to 6 cells deep in the middle of its run,
        // light head, two body tones, a dark outline cell under it; the back half one tone darker.
        const deep = 1 + Math.round(body * 4);
        set(x, y, isFront ? R[4] : R[3], c);
        for (let k = 1; k <= deep; k++) set(x, y + k * c, k <= deep / 2 ? (isFront ? R[3] : R[2]) : isFront ? R[2] : R[1], c);
        set(x, y + (deep + 1) * c, R[0], c);
      }
  });
}

/** A rising column of the element (the refs' pillars): a bright core, light sides, broken dashes up top. */
function columnPic(R: Ramp, h: number, wd: number, step: number, fade: number): Pic {
  const c = cellOf('world');
  const hb = Math.round(h / 8) * 8;
  return pic(`col|${R[0]}|${hb}|${wd}|${step}|${fade}|${c}`, -wd - 2, -hb - 4, wd + 3, 2, (set) => {
    for (let y = 0; y < hb; y += c) {
      const u = y / hb;
      // The column climbs over the steps, and breaks into dashes near its head.
      if (u > (step + 1) / 6) break;
      const half = Math.max(1, Math.round(wd * (1 - u * 0.7)));
      const dash = u > 0.55 && Math.floor((y + step * 5) / (3 * c)) % 2 === 1;
      if (dash) continue;
      for (let x = -half; x <= half; x += c) {
        if (fade > 0 && (Math.round(x / c) * 3 + Math.round(y / c)) % 4 < fade) continue;
        const e = Math.abs(x) / (half + 0.01);
        const col = e < 0.25 ? R[4] : e < 0.6 ? R[3] : e < 0.9 ? R[2] : R[0];
        set(x, -y, col, c);
      }
    }
  });
}

/** Motes rising in the element's light tones round a point (the refs' sparkle fields), a hard cross on the big ones. */
function motes(g: CanvasRenderingContext2D, t: number, sc: InkScene, R: Ramp, cx: number, cy: number, spread: number, rise: number, n: number, f: number, seed: number): void {
  const c = cellOf('world');
  for (let i = 0; i < n; i++) {
    if (gone(i, f, seed)) continue;
    const lt = (t * (0.8 + 0.6 * hash2(i, 1, seed)) + hash2(i, 2, seed)) % 1;
    const [X, Y] = P(sc, cx + (hash2(i, 3, seed) - 0.5) * spread + Math.sin(lt * 6 + i) * 3, cy - lt * rise);
    const big = i % 4 === 0;
    dot(g, lt < 0.5 ? R[4] : R[3], X, Y, 'world', big ? 2 : 1);
    if (big && lt < 0.4) {
      g.fillStyle = R[3];
      g.fillRect(Math.round(X) - 2 * c, Math.round(Y) + c * 0.5, c, c);
      g.fillRect(Math.round(X) + 3 * c, Math.round(Y) + c * 0.5, c, c);
    }
  }
}

/**
 * The bloom of a landed blow at the foe's feet `tau` s after it, by weight: medium a ground ring + a swirl round the
 * foe; heavy a second ring, a taller swirl, a rising column and motes. Light blows draw none (their sparks and burst
 * already grew with the scale pass); a crowd fight of basic cuts stays readable.
 */
export function drawHitBloom(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, R: Ramp, w: 0 | 1 | 2): void {
  if (w === 0 || tau < 0.02 || tau > 0.75) return;
  const fx = foeX(sc);
  const fy = sc.tdy;
  const fd = Math.min(3, Math.floor(span(tau, 0.4, 0.75) * 4));
  if (fd >= 3) return;
  const grow = easeOut(span(tau, 0.02, 0.25));
  if (layer === 'ground') {
    const r = Math.round(18 + (26 + 14 * w) * grow);
    drawPic(g, sc, ringPic(R, r, Math.round(r * 0.42), fd, 2), fx, fy);
    if (w === 2) {
      const r2 = Math.round(10 + 30 * easeOut(span(tau, 0.08, 0.4)));
      drawPic(g, sc, ringPic(R, r2, Math.round(r2 * 0.42), Math.min(3, fd + 1), 1), fx, fy);
    }
  }
  const phase = Math.floor(tau * 24) % PHASES;
  const rx = 20 + 8 * w;
  const h = (w === 2 ? 90 : 64) * (0.5 + 0.5 * grow);
  drawPic(g, sc, swirlPic(R, rx, h, phase, layer === 'over', fd), fx, fy);
  if (w === 2 && layer === 'over') {
    const step = Math.min(5, Math.floor(span(tau, 0.02, 0.2) * 6));
    drawPic(g, sc, columnPic(R, 120, 5, step, fd), fx, fy);
    motes(g, tau, sc, R, fx, fy - 10, 70, 110, Math.round(16 * detailQuality()), span(tau, 0.4, 0.75), 1210);
  }
}

/** A swirl round the caster on a spin, over its first 0.6 s (ring under, ribbons both sides of the body). */
export function drawSpinBloom(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, R: Ramp, w: 0 | 1 | 2): void {
  if (tau < 0 || tau > 0.6) return;
  const fd = Math.min(3, Math.floor(span(tau, 0.35, 0.6) * 4));
  if (fd >= 3) return;
  const grow = easeOut(span(tau, 0, 0.2));
  const phase = Math.floor(tau * 30) % PHASES;
  drawPic(g, sc, swirlPic(R, 30 + 10 * w, (56 + 16 * w) * (0.6 + 0.4 * grow), phase, layer === 'over', fd, 1.1), 0, 0);
  if (layer === 'ground') {
    const r = Math.round(30 + (30 + 10 * w) * grow);
    drawPic(g, sc, ringPic(R, r, Math.round(r * 0.42), fd, 2), 0, 0);
  }
}

/**
 * A body aura (heals and buffs) over `life` s: the aura sheet's four blocks round the caster — a ground ring, a
 * swirl, a rising column (heals: from the ground; buffs: none, a second ring instead), motes.
 */
export function drawAuraBloom(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, R: Ramp, heal: boolean, life: number): void {
  if (t < 0 || t > life) return;
  const fd = Math.min(3, Math.floor(span(t, life - 0.5, life) * 4));
  if (fd >= 3) return;
  const grow = easeOut(span(t, 0, 0.35));
  const phase = Math.floor(t * 16) % PHASES;
  drawPic(g, sc, swirlPic(R, 26, 70 * (0.4 + 0.6 * grow), phase, layer === 'over', fd, 1), 0, 0);
  if (layer === 'ground') {
    const r = Math.round(20 + 22 * grow);
    drawPic(g, sc, ringPic(R, r, Math.round(r * 0.42), fd, 2), 0, 0);
    if (!heal) {
      const r2 = Math.round(10 + 40 * ((t * 0.9) % 1));
      drawPic(g, sc, ringPic(R, r2, Math.round(r2 * 0.42), Math.max(fd, Math.floor(((t * 0.9) % 1) * 3)), 1), 0, 0);
    }
    return;
  }
  if (heal) {
    const step = Math.min(5, Math.floor(span(t, 0.1, 0.5) * 6));
    drawPic(g, sc, columnPic(R, 100, 4, step, Math.max(fd, t > 0.9 ? 1 : 0)), 0, 0);
  }
  motes(g, t, sc, R, 0, -6, 60, 100, Math.round(14 * detailQuality()), span(t, life - 0.5, life), 1220);
}
